import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Grid,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Header from "../DataLayouts/Header";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import axios from "axios";
import { ApplicationRoles, getOrganizationUser } from "../../Apis/Organization-User";
import { MAIN_URL } from "../../Configurations/Urls";
import { fetchUserTypes } from "../../Apis/UserTypes";
import SelectWithImage from "../../Components/Inputs/SelectWithImage";
import { fetchGeneralCountries } from "../../Apis/OrganizationLocation";
import { fetchOrganizationEmployee } from "../../Apis/Employee-api";
import { fetchInterns } from "../../Apis/InternManagement";

function UsersAdd() {
  const [countryCode, setCountryCode] = useState(null);
  const { userData } = useAuthStore();
  const [Roles, setRoles] = useState([]);
  const org = userData?.organization;
  const [userTypes, setUserTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [sharedSourceOrganizationId, setSharedSourceOrganizationId] = useState("");
  const [Employees, setEmployees] = useState([]);
  const [Interns, setInterns] = useState([]);

  useEffect(() => {
    fetchGeneralCountries()
      .then((data) => {
        const formatted = data.countries.map((item) => ({
          name: item.country_name,
          code: item.country_code,
          phone_code: item.country_phone_code,
        }));
        setCountries(formatted);
        setCountryCode(formatted.find((item) => item.code === "IN"));
      })
      .catch((err) => setFormErrors({ general: err.message }));
  }, []);

  const [userDetails, setUserDetails] = useState({
    user_name: "",
    phone: "",
    application_user_role_id: null,
    password_hash: "",
    organization_user_type_id: "",
    phone_code: "",
    organization_id: org?.organization_id,
    email: "",
    organization_person_id: "",
    application_user_id: "",
  });

  useEffect(() => {
    if (countryCode?.phone_code) {
      setUserDetails((prev) => ({
        ...prev,
        phone_code: countryCode.phone_code,
      }));
    }
  }, [countryCode]);

  useEffect(() => {
    if (!org?.organization_id) return;
    fetchOrganizationEmployee(org.organization_id)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setEmployees(
          list.filter((item) => item.employment_status !== "Exited")
        );
      })
      .catch((err) => {
        setFormErrors((prev) => ({ ...prev, general: err.message }));
      });
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id) return;
    fetchInterns(org.organization_id)
      .then((data) => {
        const interns = data?.intership?.data || data?.interns?.data || [];
        setInterns(
          interns.filter(
            (intern) =>
              intern?.status?.internship_status_name !== "Exited"
          )
        );
      })
      .catch((err) => {
        setFormErrors((prev) => ({ ...prev, general: err.message }));
      });
  }, [org?.organization_id]);

  useEffect(() => {
    fetchUserTypes(org?.organization_id)
      .then((data) => {
        setUserTypes(data?.userTypes?.data);
      })
      .catch((err) => {
        setFormErrors({ general: err.message });
      });
  }, [org?.organization_id]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!org?.organization_id) {
      return;
    }
    ApplicationRoles(org.organization_id)
      .then((data) => {
        setRoles(data?.userroles);
      })
      .catch((err) => {
        setFormErrors({ general: err.message });
      });
  }, [org?.organization_id]);

  const selectedUserType = userTypes?.find(
    (type) =>
      type.organization_user_type_id === userDetails.organization_user_type_id
  );

  const isEmployeeUser =
    selectedUserType?.user_type_name?.toLowerCase() === "employee";
  const isInternUser =
    selectedUserType?.user_type_name?.toLowerCase() === "intern";
  const isSharedResourceUser =
    selectedUserType?.user_type_name?.toLowerCase() === "shared resource";
  const sharedMaskedPassword =
    isSharedResourceUser && userDetails.application_user_id ? "********" : "";

  useEffect(() => {
    const byId = new Map();

    (userData?.metadata || [])
      .map((item) => item?.organization)
      .filter((item) => item?.organization_id)
      .forEach((item) => {
        byId.set(Number(item.organization_id), item);
      });

    const allKnownOrganizations = [
      ...(Array.isArray(userData?.organizations) ? userData.organizations : []),
      ...(Array.isArray(userData?.organization) ? userData.organization : []),
      ...(userData?.organization ? [userData.organization] : []),
    ].filter((item) => item?.organization_id);

    const knownById = new Map(
      allKnownOrganizations.map((item) => [Number(item.organization_id), item])
    );

    (userData?.metadata || [])
      .map((item) => Number(item?.organization_id))
      .filter((id) => Number.isFinite(id))
      .forEach((id) => {
        if (!byId.has(id)) {
          byId.set(id, knownById.get(id) || { organization_id: id });
        }
      });

    if (byId.size === 0 && userData?.organization?.organization_id) {
      byId.set(Number(userData.organization.organization_id), userData.organization);
    }

    setOrganizations(Array.from(byId.values()));
  }, [userData]);

  useEffect(() => {
    if (!isSharedResourceUser || !sharedSourceOrganizationId) {
      setOrganizationUsers([]);
      return;
    }
    getOrganizationUser(sharedSourceOrganizationId)
      .then((data) => {
        setOrganizationUsers(
          Array.isArray(data?.users) ? data.users : data?.users?.data || []
        );
      })
      .catch((err) => {
        setFormErrors((prev) => ({ ...prev, application_user_id: err.message }));
      });
  }, [isSharedResourceUser, sharedSourceOrganizationId]);

  const handleDetailChange = (e) => {
    const { name, value } = e.target;

    if (name === "organization_person_id") {
      const personId = parseInt(value, 10);
      const selectedEmp = Employees.find(
        (emp) => emp.organization_person_id === personId
      );
      if (selectedEmp) {
        const rawPhone =
          selectedEmp?.contact?.personal_phone_number ||
          selectedEmp?.personal_phone_number ||
          selectedEmp?.phone ||
          "";
        const rawEmail =
          selectedEmp?.contact?.personal_email ||
          selectedEmp?.personal_email ||
          selectedEmp?.email ||
          "";
        setUserDetails((prev) => ({
          ...prev,
          organization_person_id: value,
          user_name:
            selectedEmp?.name ||
            `${selectedEmp?.first_name || ""} ${selectedEmp?.last_name || ""}`.trim(),
          phone: rawPhone === "-" ? "" : rawPhone,
          email: rawEmail === "-" ? "" : rawEmail,
        }));
        return;
      }

      const selectedIntern = Interns.find(
        (i) => i.organization_person_id === personId
      );
      if (selectedIntern) {
        const rawPhone =
          selectedIntern?.contact?.personal_phone_number ||
          selectedIntern?.personal_phone_number ||
          "";
        const rawEmail =
          selectedIntern?.contact?.personal_email ||
          selectedIntern?.personal_email ||
          "";
        setUserDetails((prev) => ({
          ...prev,
          organization_person_id: value,
          user_name:
            selectedIntern?.org_persons?.person?.full_name?.trim() ||
            selectedIntern?.name ||
            "",
          phone: rawPhone === "-" ? "" : rawPhone,
          email: rawEmail === "-" ? "" : rawEmail,
        }));
      }
    } else if (name === "organization_user_type_id") {
      const selectedType = userTypes.find(
        (type) => type.organization_user_type_id === parseInt(value, 10)
      );
      const isEmployee =
        selectedType?.user_type_name?.toLowerCase() === "employee";
      const isIntern =
        selectedType?.user_type_name?.toLowerCase() === "intern";
      const isShared =
        selectedType?.user_type_name?.toLowerCase() === "shared resource";
      const linksPerson = isEmployee || isIntern;

      setUserDetails((prev) => ({
        ...prev,
        organization_user_type_id: value,
        organization_id: org?.organization_id,
        organization_person_id: linksPerson ? prev.organization_person_id : "",
        application_user_id: isShared ? prev.application_user_id : "",
        user_name: linksPerson ? prev.user_name : "",
        phone: linksPerson ? prev.phone : "",
        email: linksPerson ? prev.email : "",
        password_hash: isShared ? "" : prev.password_hash,
      }));
      if (!isShared) {
        setSharedSourceOrganizationId("");
        setOrganizationUsers([]);
      }
    } else if (name === "organization_id") {
      setSharedSourceOrganizationId(value);
      setUserDetails((prev) => ({
        ...prev,
        application_user_id: "",
        user_name: "",
        phone: "",
        email: "",
      }));
    } else if (name === "application_user_id") {
      const selectedOrgUser = organizationUsers.find(
        (item) => String(item?.application_user_id) === String(value)
      );
      const appUser = selectedOrgUser?.applicationuser?.[0];
      setUserDetails((prev) => ({
        ...prev,
        application_user_id: value,
        user_name: appUser?.full_name || "",
        phone: appUser?.phone_number || "",
        email: appUser?.email || "",
        password_hash: value ? "********" : "",
      }));
    } else if (name === "organization_user_role_id") {
      const selectedRole = Roles.find(
        (role) => String(role.organization_user_role_id) === String(value)
      );
      setUserDetails((prev) => ({
        ...prev,
        organization_user_role_id: value,
        application_user_role_id:
          selectedRole?.application_user_role_id != null
            ? Number(selectedRole.application_user_role_id)
            : null,
      }));
      setFormErrors((prev) => ({ ...prev, organization_user_role_id: "" }));
    } else {
      setUserDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};

    if (!userDetails.user_name) errors.user_name = "User's Name is required.";
    if (!userDetails.phone) errors.phone = "Phone is required.";
    if (!userDetails.organization_user_type_id)
      errors.organization_user_type_id = "User Type is required.";
    if (!userDetails.organization_user_role_id)
      errors.organization_user_role_id = "Role is required.";

    if ((isEmployeeUser || isInternUser) && !userDetails.organization_person_id) {
      errors.organization_person_id = isEmployeeUser
        ? "Employee is required."
        : "Intern is required.";
    }

    if (isSharedResourceUser && !sharedSourceOrganizationId) {
      errors.organization_id = "Organization is required.";
    }
    if (isSharedResourceUser && !userDetails.application_user_id) {
      errors.application_user_id = "User is required.";
    }
    if (userDetails.email && !isValidEmail(userDetails.email)) {
      errors.email = "Enter a valid email address.";
    }
    if (!userDetails.email) errors.email = "Email is required.";
    if (!isSharedResourceUser && !userDetails.password_hash) {
      errors.password_hash = "Password is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFinalSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);

    try {
      const targetOrgId = org?.organization_id;
      const endpoint = isSharedResourceUser
        ? `${MAIN_URL}/api/organizations/${targetOrgId}/user/create-from-existing-user`
        : `${MAIN_URL}/api/organizations/${targetOrgId}/user/create-new-user`;

      const payload = isSharedResourceUser
        ? {
            source_organization_id: Number(sharedSourceOrganizationId),
            application_user_id: Number(userDetails.application_user_id),
            organization_user_type_id: Number(userDetails.organization_user_type_id),
            application_user_role_id: userDetails.application_user_role_id
              ? Number(userDetails.application_user_role_id)
              : null,
            organization_person_id: userDetails.organization_person_id
              ? Number(userDetails.organization_person_id)
              : null,
          }
        : {
            ...userDetails,
            organization_user_role_id: userDetails.organization_user_role_id
              ? Number(userDetails.organization_user_role_id)
              : null,
            application_user_role_id: userDetails.application_user_role_id
              ? Number(userDetails.application_user_role_id)
              : null,
            phone_verified: 0,
            is_active: 1,
          };

      await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("User created successfully!");
      setFormErrors({});
      navigate("/users");
    } catch (err) {
      console.error(err);

      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);

        const errorMessages = Object.values(validationErrors)
          .map((arr) => arr[0])
          .join(" ");

        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Box px={4} py={4} minHeight="100vh">
      <Header
        mode="add"
        updateMessage="User"
        addMessage="User"
        homeLink="/users"
        homeText="Users"
      />

      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <TextField
            select
            fullWidth
            label="User Type"
            name="organization_user_type_id"
            value={userDetails.organization_user_type_id}
            onChange={handleDetailChange}
            error={!!formErrors.organization_user_type_id}
            helperText={formErrors.organization_user_type_id}
            disabled={userTypes?.length === 0}
            required
            sx={{ textTransform: "capitalize" }}
          >
            {userTypes?.map((option) => (
              <MenuItem
                key={option?.organization_user_type_id}
                value={option?.organization_user_type_id}
                sx={{ textTransform: "capitalize" }}
              >
                {option?.user_type_name}
              </MenuItem>
            ))}
          </TextField>

          {isSharedResourceUser && (
            <TextField
              select
              fullWidth
              label="Organization"
              name="organization_id"
              value={sharedSourceOrganizationId}
              onChange={handleDetailChange}
              error={!!formErrors.organization_id}
              helperText={formErrors.organization_id}
              required
            >
              {organizations?.map((option) => (
                <MenuItem key={option.organization_id} value={option.organization_id}>
                  {option.organization_name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isSharedResourceUser && (
            <TextField
              select
              fullWidth
              label="Organization User"
              name="application_user_id"
              value={userDetails.application_user_id}
              onChange={handleDetailChange}
              error={!!formErrors.application_user_id}
              helperText={formErrors.application_user_id}
              required
            >
              {organizationUsers?.map((option) => (
                <MenuItem
                  key={option?.organization_user_id}
                  value={option?.application_user_id}
                >
                  {option?.applicationuser?.[0]?.full_name || "Unknown User"}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isEmployeeUser && (
            <Autocomplete
              fullWidth
              options={Employees || []}
              getOptionLabel={(option) =>
                `${option?.name || ""} - ID: ${option?.employee_code || ""}`.trim()
              }
              value={
                Employees?.find(
                  (emp) =>
                    String(emp?.organization_person_id) ===
                    String(userDetails?.organization_person_id)
                ) || null
              }
              onChange={(event, newValue) => {
                handleDetailChange({
                  target: {
                    name: "organization_person_id",
                    value: newValue?.organization_person_id || "",
                  },
                });
              }}
              isOptionEqualToValue={(option, value) =>
                option?.organization_person_id ===
                value?.organization_person_id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employee"
                  name="organization_person_id"
                  required
                  error={!!formErrors?.organization_person_id}
                  helperText={formErrors?.organization_person_id}
                  fullWidth
                />
              )}
            />
          )}

          {isInternUser && (
            <Autocomplete
              fullWidth
              options={Interns || []}
              getOptionLabel={(option) =>
                `${option?.org_persons?.person?.full_name || ""} (${option?.org_persons?.person_code || ""})`.trim()
              }
              value={
                Interns?.find(
                  (i) =>
                    String(i?.organization_person_id) ===
                    String(userDetails?.organization_person_id)
                ) || null
              }
              onChange={(event, newValue) => {
                handleDetailChange({
                  target: {
                    name: "organization_person_id",
                    value: newValue?.organization_person_id || "",
                  },
                });
              }}
              isOptionEqualToValue={(option, value) =>
                option?.organization_person_id ===
                value?.organization_person_id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Intern"
                  name="organization_person_id"
                  required
                  error={!!formErrors?.organization_person_id}
                  helperText={formErrors?.organization_person_id}
                  fullWidth
                />
              )}
            />
          )}

          <TextField
            select
            fullWidth
            label="User Role"
            name="organization_user_role_id"
            value={userDetails.organization_user_role_id ?? ""}
            onChange={handleDetailChange}
            error={!!formErrors.organization_user_role_id}
            helperText={formErrors.organization_user_role_id}
            disabled={!Roles?.length}
            required
          >
            {Roles?.map((option) => (
              <MenuItem
                key={option.organization_user_role_id}
                value={String(option.organization_user_role_id)}
              >
                {option.user_role_name || option.role_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            value={userDetails.user_name}
            error={!!formErrors.user_name}
            helperText={formErrors.user_name}
            fullWidth
            label="Full Name"
            name="user_name"
            onChange={handleDetailChange}
            required
            disabled={isEmployeeUser || isInternUser}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              width: "100%",
            }}
          >
            <div style={{ width: "25%" }}>
              <div
                style={{
                  border: "2px solid rgba(0, 0, 0, 0.23)",
                  borderRadius: "6px",
                }}
              >
                <SelectWithImage
                  value={countryCode}
                  error={false}
                  options={countries}
                  optionkey="phone_code"
                  onChange={(e, val) => setCountryCode(val)}
                  width="100%"
                  minWidth="20px"
                  disabled={false}
                  searchKeys={["name", "phone_code"]}
                />
              </div>
            </div>

            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={userDetails.phone === "-" ? "" : userDetails.phone}
              error={!!formErrors.phone}
              helperText={
                formErrors.phone ||
                (isEmployeeUser || isInternUser
                  ? "You can edit the phone number before creating the user."
                  : "")
              }
              onChange={handleDetailChange}
              required
              inputProps={{
                maxLength: 20,
                inputMode: "numeric",
                onInput: (e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                },
              }}
            />
          </div>

          <TextField
            fullWidth
            label="Email"
            type="email"
            name="email"
            value={userDetails.email === "-" ? "" : userDetails.email}
            error={!!formErrors.email}
            helperText={
              formErrors.email ||
              (isEmployeeUser || isInternUser
                ? "You can edit the email before creating the user."
                : "")
            }
            onChange={handleDetailChange}
            required
          />

          <TextField
            fullWidth
            label="Password"
            name="password_hash"
            type="password"
            value={isSharedResourceUser ? sharedMaskedPassword : userDetails.password_hash}
            error={!!formErrors.password_hash}
            helperText={
              isSharedResourceUser
                ? "Password is encrypted for organization user and cannot be viewed."
                : formErrors.password_hash ||
                  (isEmployeeUser || isInternUser
                    ? "Set the login password for this user."
                    : "")
            }
            onChange={handleDetailChange}
            required={!isSharedResourceUser}
            disabled={isSharedResourceUser}
            InputProps={{ readOnly: false }}
          />

          <Button
            variant="contained"
            onClick={handleFinalSubmit}
            disabled={btnLoading}
          >
            {btnLoading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Create User"
            )}
          </Button>
        </Grid>
      </Paper>
    </Box>
  );
}

export default UsersAdd;
